import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { Eye, EyeOff } from 'lucide-react';

import AuthLayout from '../../shared/components/AuthLayout';
import Button from '../../shared/components/Button';
import Input from '../../shared/components/Input';
import LoginOption from '../../components/LoginOption';
import OverlayLoader from '../../components/Loader/OverlayLoader';
import LoadingSpinner from '../../components/LoadingSpinner/LoadingSpinner';
import loginImage from '../../assets/images/signup.png';
import { useAuth } from '../../shared/hooks/useAuth';
import { useAuthForm } from '../../shared/hooks/useAuthForm';
import { useSocialAuth } from '../../shared/hooks/useSocialAuth';
import { AUTH_STACK, DASHBOARD_STACK } from '../../utils/screenNames';
import {
  loginWithEmailPhone,
  getUserProfile,
  refreshTokens,
} from '../../redux/actions/authActions';
import { updateUserInfo } from '../../redux/slices/userSlice';
import { isTokenExpired } from '../../utils/helpers';

/**
 * LoginOptions Page - Refactored with Tailwind CSS
 * Main login page with social login options and inline email/password form
 */
function LoginOptions() {
  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const { handleAuthSuccess } = useAuth();
  const {
    values,
    errors,
    handleChange,
    handleBlur,
    validateEmail,
    validatePassword,
    validate,
    setError,
  } = useAuthForm({ email: '', password: '' });
  const { signInWithProvider, isSocialLoading } = useSocialAuth((message) =>
    setError('email', message)
  );

  const [isLoading, setIsLoading] = useState(true);
  const [isSelected, setIsSelected] = useState(location.state?.showEmailForm || false);
  const [isLoginLoading, setIsLoginLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  /**
   * Handle token refresh
   */
  const handleResetTokens = useCallback(
    async (refreshToken) => {
      async function onSuccess(data) {
        const { access, refresh } = data;
        window.localStorage.setItem('access', JSON.stringify(access));
        window.localStorage.setItem('refresh', JSON.stringify(refresh));

        const userData = await getUserProfile(access);
        dispatch(updateUserInfo({ userData }));
        navigate(DASHBOARD_STACK.HOME);
      }

      function onFail() {
        setIsLoading(false);
      }

      await refreshTokens(refreshToken, onSuccess, onFail);
    },
    [dispatch, navigate]
  );

  /**
   * Check if user is already logged in
   */
  useEffect(() => {
    const setUserProfile = async () => {
      const accessString = window.localStorage.getItem('access');
      const refreshString = window.localStorage.getItem('refresh');

      if (accessString) {
        const access = JSON.parse(accessString);
        if (isTokenExpired(access.expiry)) {
          const refresh = JSON.parse(refreshString);
          handleResetTokens(refresh);
        } else {
          try {
            const userData = await getUserProfile(access);
            dispatch(updateUserInfo({ userData }));
            navigate(DASHBOARD_STACK.HOME);
          } catch (err) {
            setIsLoading(false);
          }
        }
      } else {
        setIsLoading(false);
      }
    };

    setUserProfile();
  }, [navigate, dispatch, handleResetTokens]);

  /**
   * Handle Email/Password Login
   */
  const handleLogin = async () => {
    // Validate form
    const isValid = validate({
      email: validateEmail,
      password: validatePassword,
    });

    if (!isValid) return;

    setIsLoginLoading(true);

    try {
      await loginWithEmailPhone(
        values.email,
        null,
        values.password,
        (userData) => {
          handleAuthSuccess(userData, undefined, { loginMethod: 'email', isSignup: false });
        },
        (errMsg) => {
          setError('password', errMsg || 'Login failed');
          setIsLoginLoading(false);
        }
      );
    } catch (error) {
      setError('password', 'An error occurred. Please try again.');
      setIsLoginLoading(false);
    }
  };

  if (isLoading || isSocialLoading) {
    return <OverlayLoader />;
  }

  return (
    <div className="relative w-screen h-screen bg-app-bg">
      {/* Main Content */}
      <AuthLayout
        heroImage={loginImage}
        title="Welcome back!"
        subtitle="Log in to your Clubbie account"
      >
        {isSelected ? (
          /* Email/Password Form */
          <>
            {/* Email Input */}
            <div className="w-full">
              <Input
                type="email"
                name="email"
                placeholder="Email or user name"
                value={values.email}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.email}
                errorMessage={errors.email}
              />
            </div>

            {/* Password Input */}
            <div className="w-full mt-4">
              <Input
                type={showPassword ? 'text' : 'password'}
                name="password"
                placeholder="Password"
                value={values.password}
                onChange={handleChange}
                onBlur={handleBlur}
                error={!!errors.password}
                errorMessage={errors.password}
                rightIcon={
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="text-app-text-secondary hover:text-[#FEFCEB] transition-colors"
                  >
                    {showPassword ? (
                      <EyeOff className="w-5 h-5" />
                    ) : (
                      <Eye className="w-5 h-5" />
                    )}
                  </button>
                }
              />
            </div>

            {/* Forgot Password Link */}
            <button
              onClick={() => navigate(AUTH_STACK.FORGOT_PASSWORD_WITH_EMAIL)}
              className="w-full text-left text-sm text-app-text-secondary hover:text-light-blue transition-colors font-proxima mt-2"
            >
              Forgot password?
            </button>

            {/* Login Button */}
            <Button
              size="lg"
              onClick={handleLogin}
              disabled={isLoginLoading}
              className="flex-1 w-full h-11 bg-[#00E7FF] mt-5 text-[#021529] rounded-full font-medium text-[15px] hover:opacity-50 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {isLoginLoading ? <><LoadingSpinner size="small" /><span>Logging in...</span></> : "Continue"}
            </Button>

            {/* Back to options */}
            <button
              onClick={() => setIsSelected(false)}
              className="w-full text-center text-sm text-app-text-secondary hover:text-light-blue transition-colors font-proxima mt-4"
            >
              Choose another login method
            </button>
          </>
        ) : (
          /* Social Login Options */
          <>
            <div className="w-full flex flex-col gap-3">
              <LoginOption
                optionType="email"
                optionText="Continue with email"
                onOptionClick={() => setIsSelected(true)}
              />
              <LoginOption
                optionType="google"
                optionText="Continue with Google"
                onOptionClick={() => signInWithProvider('google')}
              />
              <LoginOption
                optionType="apple"
                optionText="Continue with Apple"
                onOptionClick={() => signInWithProvider('apple')}
              />
            </div>
          </>
        )}

        {/* Sign Up Link */}
        <p className="text-center text-sm text-app-text-secondary mt-6 font-proxima">
          Don't have an account?{' '}
          <button
            onClick={() => navigate(AUTH_STACK.SIGNUP_OPTIONS)}
            className="text-[#FEFCEB] underline hover:text-light-blue transition-colors"
          >
            Sign up
          </button>
        </p>

        {/* Terms & Privacy */}
        <p className="text-center text-xs text-app-text-secondary mt-6 font-proxima leading-relaxed">
          By continuing, you agree to our{' '}
          <a href="https://clubbie.com/service/terms-of-service" target="_blank" rel="noopener noreferrer" className="underline text-app-text hover:text-light-blue">
            Terms of Service
          </a>{' '}
          and acknowledge that you have read our{' '}
          <a href="https://clubbie.com/service/privacy-policy" target="_blank" rel="noopener noreferrer" className="underline text-app-text hover:text-light-blue">
            Privacy Policy
          </a>{' '}
          to learn how we collect, use and share your data.
        </p>
      </AuthLayout>
    </div>
  );
}

export default LoginOptions;
